import * as d3 from "d3";
import type { Entry, KalshiCandle, Synergy } from "../types";

type SparklineProps = {
  candles: KalshiCandle[];
  width?: number;
  height?: number;
  color?: string;
};

export function Sparkline({ candles, width = 310, height = 82, color = "var(--cyan)" }: SparklineProps) {
  const points = candles
    .map((d) => ({ x: d.end_period_ts, y: d.price_close ?? d.price_mean ?? d.price_open }))
    .filter((d): d is { x: number; y: number } => d.y !== null && Number.isFinite(d.y));

  if (points.length < 2) {
    return <div className="empty">No market movement yet</div>;
  }

  const x = d3.scaleLinear(d3.extent(points, (d) => d.x) as [number, number], [8, width - 8]);
  const yExtent = d3.extent(points, (d) => d.y) as [number, number];
  const y = d3.scaleLinear([Math.max(0, yExtent[0] - 0.01), Math.min(1, yExtent[1] + 0.01)], [height - 12, 10]);
  const line = d3
    .line<{ x: number; y: number }>()
    .x((d) => x(d.x))
    .y((d) => y(d.y))
    .curve(d3.curveMonotoneX);
  const area = d3
    .area<{ x: number; y: number }>()
    .x((d) => x(d.x))
    .y0(height - 10)
    .y1((d) => y(d.y))
    .curve(d3.curveMonotoneX);

  const last = points[points.length - 1];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Kalshi price sparkline">
      <defs>
        <linearGradient id="spark-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area(points) || ""} fill="url(#spark-area)" />
      <path d={line(points) || ""} fill="none" stroke={color} strokeWidth="2.5" />
      <circle cx={x(last.x)} cy={y(last.y)} r="3.5" fill={color} />
    </svg>
  );
}

type RadarProps = {
  entry: Entry;
  size?: number;
};

export function SignalRadar({ entry, size = 260 }: RadarProps) {
  const axes = [
    { key: "Manual", value: (entry.manual_jury + entry.manual_audience + entry.manual_friendliness) / 30, color: "var(--gold)" },
    { key: "Market", value: entry.market_score, color: "var(--cyan)" },
    { key: "Poll", value: entry.poll_score, color: "var(--green)" },
    { key: "Spotify", value: entry.spotify_score, color: "var(--coral)" },
    { key: "YouTube", value: entry.youtube_score, color: "var(--violet)" },
    { key: "History", value: entry.total_synergy_score, color: "var(--muted)" },
  ];
  const center = size / 2;
  const radius = size * 0.38;
  const angle = d3.scaleLinear([0, axes.length], [-Math.PI / 2, Math.PI * 1.5]);
  const rings = [0.25, 0.5, 0.75, 1];

  const point = (index: number, value: number) => {
    const a = angle(index);
    return [center + Math.cos(a) * radius * value, center + Math.sin(a) * radius * value] as const;
  };

  const polygon = axes.map((axis, index) => point(index, axis.value)).join(" ");

  return (
    <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${entry.country} signal radar`}>
      {rings.map((ring) => (
        <polygon
          key={ring}
          points={axes.map((_, index) => point(index, ring)).join(" ")}
          fill="none"
          stroke="var(--line-soft)"
          strokeWidth="1"
        />
      ))}
      {axes.map((axis, index) => {
        const [x, y] = point(index, 1.1);
        const [x2, y2] = point(index, 1);
        return (
          <g key={axis.key}>
            <line x1={center} y1={center} x2={x2} y2={y2} stroke="var(--line-soft)" />
            <text x={x} y={y} fill="var(--muted)" fontSize="10" textAnchor={x < center - 8 ? "end" : x > center + 8 ? "start" : "middle"}>
              {axis.key}
            </text>
          </g>
        );
      })}
      <polygon points={polygon} fill="oklch(0.82 0.145 82 / 0.2)" stroke="var(--gold)" strokeWidth="2" />
      {axes.map((axis, index) => {
        const [x, y] = point(index, axis.value);
        return <circle key={axis.key} cx={x} cy={y} r="3.5" fill={axis.color} />;
      })}
    </svg>
  );
}

export function ProbabilityBars({ entry }: { entry: Entry }) {
  const rows = [
    ["Win", entry.winner_probability, "var(--gold)"],
    ["Top 3", entry.top3_probability, "var(--cyan)"],
    ["Top 5", entry.top5_probability, "var(--green)"],
    ["Top 10", entry.top10_probability, "var(--violet)"],
    ["Top 15", entry.top15_probability, "var(--coral)"],
  ] as const;

  return (
    <div className="top-five">
      {rows.map(([label, value, color]) => (
        <div key={label} className="mini-meter" style={{ gridTemplateColumns: "58px 1fr 48px" }}>
          <span>{label}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${Math.max(1, value * 100)}%`, background: color }} />
          </div>
          <span className="num">{(value * 100).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

export function SynergyBars({ synergies }: { synergies: Synergy[] }) {
  const top = synergies.slice(0, 7);
  if (!top.length) return <div className="empty">No historical synergy rows</div>;
  const max = d3.max(top, (d) => d.total_points_avg) || 1;

  return (
    <div className="top-five">
      {top.map((item) => (
        <div key={`${item.from_country_id}-${item.to_country_id}`} className="mini-meter" style={{ gridTemplateColumns: "92px 1fr 42px" }}>
          <span title={item.from_country}>{item.from_country}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(item.total_points_avg / max) * 100}%`, background: "var(--gold)" }} />
          </div>
          <span className="num">{item.total_points_avg.toFixed(1)}</span>
        </div>
      ))}
    </div>
  );
}
